/**
 * The portal's write rules, shared by every page that writes (place manager
 * units 6b and 6c, scheduling and changing events; unit 8, the games a brand's
 * places run): a double press sends once ({@link singleFlight}); one write at a
 * time per page ({@link oneWriteAtATime}); an undo for five minutes
 * ({@link UNDO_MS}); after a lost answer, the page reads again at once, in a
 * few seconds and in half a minute ({@link REREADS_AFTER_LOSS_MS}), and offers
 * no second send; a failure said in plain words ({@link failureLine}).
 *
 * Pure: no React, no transport. Whether a failure is lost (`lost-answer.ts`)
 * needs the transport's error classes, so it lives apart; the hooks live in
 * `use-writes.ts`.
 */
import { BackendUnavailableError, isMaskedMessage } from "@/lib/backend-error";

const MINUTE = 60 * 1000;

/** How long "Undo" stays after a write. */
export const UNDO_MS = 5 * MINUTE;

/** Whether the five minutes to undo a write done at `madeAt` are still
 *  running at `now`: the one time rule for "Undo" (what shows it, what ticks
 *  for it, what sends it). */
export function undoWindowOpen(done: { madeAt: number }, now: number): boolean {
  return now - done.madeAt < UNDO_MS;
}

/** Whether "Undo" is offered for a write done at `madeAt` at `now`: within
 *  its five minutes, and its undo ready, or failed (it may be pressed again).
 *  Any other state (going, done, or one a page settles) offers none. */
export function undoOffered(done: { madeAt: number; undo: unknown }, now: number): boolean {
  const undo = done.undo;
  return undoWindowOpen(done, now) && (undo === "ready" || (typeof undo === "object" && undo !== null && "failed" in undo));
}

/** `fn`, one call at a time: a call while one is in flight gets that call's
 *  answer (so a second press can never send a second write). */
export function singleFlight<A extends unknown[], R>(fn: (...args: A) => Promise<R>): (...args: A) => Promise<R> {
  let current: Promise<R> | null = null;
  return (...args) => {
    if (!current) {
      current = fn(...args).finally(() => {
        current = null;
      });
    }
    return current;
  };
}

/** What a write refused because another is still going says. */
export const ONE_WRITE_LINE = "Another change here is still going. Try again once it is done.";

/** One write at a time on a page, of any kind (a send, a change, an undo). */
export interface OneWrite {
  /** Send it, unless another is going: then refused with {@link ONE_WRITE_LINE}
   *  (so no answer ever lands on a record a newer write replaced). */
  run<T>(send: () => Promise<T>): Promise<T>;
  /** Whether a write is going now. */
  readonly busy: boolean;
}

/** A page's one-write-at-a-time guard (a double press of the same button is
 *  {@link singleFlight}'s: it gets the first's answer). */
export function oneWriteAtATime(): OneWrite {
  let writing = false;
  return {
    run<T>(send: () => Promise<T>): Promise<T> {
      if (writing) return Promise.reject(new Error(ONE_WRITE_LINE));
      writing = true;
      // The executor runs `send` now and turns a throw before it returns a
      // promise (or a non-promise answer) into one, so the guard always frees.
      return new Promise<T>((resolve) => resolve(send())).finally(() => {
        writing = false;
      });
    },
    get busy() {
      return writing;
    },
  };
}

/** When a page reads again after a lost answer, past the read at once: shortly,
 *  then once more later (a save can finish after its answer was lost, e.g.
 *  behind a gateway timeout). */
export const CHECKS_AFTER_MS = [3_000, 30_000];

/** Every read again after a lost answer: at once, then {@link CHECKS_AFTER_MS}. */
export const REREADS_AFTER_LOSS_MS = [0, ...CHECKS_AFTER_MS];

/** What a write needs besides its call: how the page reads again after a lost
 *  answer ({@link REREADS_AFTER_LOSS_MS}), and whether a failure may have gone
 *  through (`isLostAnswer`, kept out of this module: it loads the transport). */
export interface WriteGuards {
  reread: () => void;
  isLost: (error: unknown) => boolean;
}

/** What failed, as {@link failureLine}'s sentence ends: one list, so the
 *  pages' wording cannot drift. */
export type Doing = "make it" | "undo it" | "change it" | "cancel it" | "make it again" | "change the games" | "read the games";

/** What a failed write or undo says: the backend's own message, in a plain
 *  sentence; never a stack. Signed out and unreachable get their own lines. */
export function failureLine(error: unknown, doing: Doing): string {
  // The first line only: a message never carries a stack here.
  const message = error instanceof Error ? (error.message.trim().split("\n")[0] ?? "").trim() : "";
  if (/signed in|sign in|unauthenticated|not authenticated/i.test(message)) {
    return `You are signed out. Sign in again to ${doing}.`;
  }
  if (error instanceof BackendUnavailableError) {
    if (error.status === 401) return `You are signed out. Sign in again to ${doing}.`;
    if (error.status !== undefined && error.status < 500) return `pop.in refused to ${doing} (HTTP ${error.status}).`;
    return `Could not reach pop.in to ${doing}. Please try again in a moment.`;
  }
  // The backend masks unexpected failures (and reads over its cost limit); several are joined by ", ".
  if (message && message.split(", ").every(isMaskedMessage)) return `pop.in could not ${doing} and gave no reason. Please try again in a moment.`;
  if (!message) return `Could not ${doing}. Please try again in a moment.`;
  return `pop.in could not ${doing}: ${message.replace(/\.?$/, ".")}`;
}
